import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { createFeature, createReducer, on } from '@ngrx/store';
import { Catalog, ProspectExtraFieldTemplateEntry } from '@niko-crm/ui';
import { CatalogActions } from './catalog.actions';
import { LoggedUserActions } from '../auth/logged-user.actions';

export interface CatalogState extends EntityState<Catalog> {
  total: number;
  page: number;
  limit: number;
  loading: boolean;
  error: string | null;
  /** Tenant-wide prospect extra-field template (always four entries once loaded). */
  extraFieldsTemplate: ProspectExtraFieldTemplateEntry[];
  extraFieldsTemplateLoaded: boolean;
  extraFieldsTemplateLoading: boolean;
}

export const catalogsAdapter: EntityAdapter<Catalog> = createEntityAdapter<Catalog>();

const initialState: CatalogState = catalogsAdapter.getInitialState({
  total: 0,
  page: 1,
  limit: 20,
  loading: false,
  error: null,
  extraFieldsTemplate: [],
  extraFieldsTemplateLoaded: false,
  extraFieldsTemplateLoading: false,
});

export const catalogReducer = createReducer(
  initialState,
  on(CatalogActions.loadCatalogs, (state) => ({ ...state, loading: true, error: null })),
  on(CatalogActions.loadCatalogsSuccess, (state, { result }) =>
    catalogsAdapter.setAll(result.data, {
      ...state,
      total: result.total,
      page: result.page,
      limit: result.limit,
      loading: false,
    }),
  ),
  on(CatalogActions.loadCatalogsFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),
  on(CatalogActions.createCatalogSuccess, (state, { catalog }) =>
    catalogsAdapter.addOne(catalog, state),
  ),
  on(CatalogActions.updateCatalogSuccess, (state, { catalog }) =>
    catalogsAdapter.updateOne({ id: catalog.id, changes: catalog }, state),
  ),
  on(CatalogActions.deleteCatalogSuccess, (state, { id }) =>
    catalogsAdapter.removeOne(id, state),
  ),
  on(CatalogActions.loadProspectExtraFieldsTemplate, (state) => ({
    ...state,
    extraFieldsTemplateLoading: true,
    error: null,
  })),
  on(CatalogActions.loadProspectExtraFieldsTemplateSuccess, (state, { fields }) => ({
    ...state,
    extraFieldsTemplate: fields,
    extraFieldsTemplateLoaded: true,
    extraFieldsTemplateLoading: false,
  })),
  on(CatalogActions.loadProspectExtraFieldsTemplateFailure, (state, { error }) => ({
    ...state,
    extraFieldsTemplateLoading: false,
    error,
  })),
  on(CatalogActions.setProspectExtraFieldsTemplate, (state, { fields }) => ({
    ...state,
    extraFieldsTemplate: fields,
    extraFieldsTemplateLoaded: true,
  })),
  on(CatalogActions.clearCatalogs, (state) =>
    catalogsAdapter.removeAll({ ...state, total: 0, page: 1, error: null }),
  ),
  // Logout navigates client-side (`router.navigate(['/login'])`), so the bundle
  // is never reloaded and this slice would survive into the next session. That
  // matters for `extraFieldsTemplate`: it is tenant-wide, and
  // `extraFieldsTemplateLoaded` makes callers SKIP the refetch, so the next
  // tenant's user would be shown the previous tenant's labels. Mirrors
  // `logged-user.reducer.ts`. (Switching business uses `location.assign`, a
  // full reload, so that path was never affected.)
  on(LoggedUserActions.logoutSuccess, () => initialState),
);

export const catalogsFeature = createFeature({
  name: 'catalogs',
  reducer: catalogReducer,
});
